import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  styled,
  Grid,
  Divider,
  Stack,
} from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  theme,
  WsChip,
  WsChipVariants,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../../../../src/context';
import { firingAmplitudeEvents } from '../../../../../features/candidates/utils/CandidateHelper';
import Branding from '../../../../../images/jobBoardsIcons/Branding.png';
import CareerBuilder from '../../../../../images/jobBoardsIcons/CareerBuilder.png';
import cv from '../../../../../images/jobBoardsIcons/cv.png';
import CWJob from '../../../../../images/jobBoardsIcons/CWJob.png';
import Dice from '../../../../../images/jobBoardsIcons/Dice.png';
import JobisiteNew from '../../../../../images/jobBoardsIcons/JobisiteNew.png';
import JobServe from '../../../../../images/jobBoardsIcons/JobServe.png';
import MonsterIcon from '../../../../../images/jobBoardsIcons/Monster.png';
import TechFetchIcon from '../../../../../images/jobBoardsIcons/TechFetchIcon.jpeg';
import ErrorLogger from '../../../../../providers/ErrorLogger';
import { IJobBoard } from '../../../../jobs/publish/components/freeJobBoards/freeJobBoardCard/FreeJobBoardCard';

const BoxNavTheme = styled(Box)(() => ({
  borderRadius: 4,
  maxWidth: 340,
  backgroundColor: WsColors.White,
  margin: theme.spacing(2, 2, 2, 0),
}));

const jobBoardsListUS: IJobBoard[] = [
  {
    name: 'Dice',
    label: '20',
    icon: <img src={Dice} alt="Dice" height={25} width={26} />,
    url: '/native-search/dice',
  },
  {
    name: 'Tech Fetch',
    label: '20',
    icon: <img src={TechFetchIcon} alt="Tech Fetch Icon" height={30} width={28} />,
    url: '/native-search/techfetch',
  },
  {
    name: 'Monster US & CA',
    label: '20',
    icon: <img src={MonsterIcon} alt="Tech Fetch Icon" height={30} width={28} />,
    url: '/native-search/monster?loc=us,canada',
  },
  {
    name: 'Career Builder',
    label: '20',
    icon: <img src={CareerBuilder} alt="Career Builder" height={30} width={28} />,
    url: '/native-search/careerbuilder',
  },
  {
    name: 'CareerBuilder Edge',
    label: '20',
    icon: <img src={CareerBuilder} alt="CareerBuilder Edge" height={30} width={28} />,
    url: '/native-search/careerbuilderedge',
  },
];

const jobBoardsListUK: IJobBoard[] = [
  {
    name: 'Monster',
    label: '20',
    icon: <img src={MonsterIcon} alt="Tech Fetch Icon" height={30} width={28} />,
    url: '/native-search/monster?loc=europe,uk',
  },
  {
    name: 'CV Library',
    label: '20',
    icon: <img src={cv} alt="CVLibraryIcon" height={28} width={28} />,
    url: '/native-search/cvlibrary',
  },
  {
    name: 'Job Serve',
    label: '20',
    icon: <img src={JobServe} alt="JobServeIcon" height={28} width={28} />,
    url: '/native-search/jobServe',
  },
  {
    name: 'Job Site',
    label: '20',
    icon: <img src={JobisiteNew} alt="JobisiteIcon" height={28} width={28} />,
    url: '/native-search/jobsite',
  },
  {
    name: 'CW Jobs',
    label: '20',
    icon: <img src={CWJob} alt="CWJobsIcon" height={28} width={28} />,
    url: '/native-search/cwJobs',
  },
];

interface JobListSideNavProps {
  selectedIndexValue: (index: number, value: string) => void;
}
type urlParams = {
  source?: string;
  encoded?: string;
  jobId?: string;
};
const TalentPoolListSideNav = ({ selectedIndexValue }: JobListSideNavProps) => {
  const params = useParams<urlParams>();
  const workspace = useWorkspaceContext();
  const talentPool: IJobBoard[] = [
    {
      name: 'Talent Pool',
      label: '',
      icon: <></>,
      url: `/candidates/${params.jobId ?? ''}`,
    },
    {
      name: 'Pipeline',
      label: '',
      icon: <></>,
      url: `/candidates/mappings/${params.source ?? ''}${params.source ? '/' : ''}${
        params.encoded ?? ''
      }`,
    },
    {
      name: 'Active Consultants',
      label: '',
      icon: <></>,
      url: '/active-consultants/',
    },
  ];

  const firingJobAmplitudeEvent = {
    ['Dice']: () => firingAmplitudeEvents('Talentpool_Dice', 'Dice', workspace, 'Candidates'),

    ['Tech Fetch']: () =>
      firingAmplitudeEvents('Talentpool_TechFetch', 'TechFetch', workspace, 'Candidates'),

    ['Monster']: () =>
      firingAmplitudeEvents('Talentpool_Monster', 'Monster', workspace, 'Candidates'),

    ['Career Builder']: () =>
      firingAmplitudeEvents('Talentpool_CareerBuilder', 'Career Builder', workspace, 'Candidates'),

    ['CareerBuilder Edge']: () =>
      firingAmplitudeEvents(
        'Talentpool_CareerBuilderEdge',
        'CareerBuilder Edge',
        workspace,
        'Candidates'
      ),

    ['CV Library']: () =>
      firingAmplitudeEvents('Talentpool_CV Library', 'CV Library', workspace, 'Candidates'),

    ['Job Serve']: () =>
      firingAmplitudeEvents('Talentpool_Job Serve', 'CV Library', workspace, 'Candidates'),
    ['Job Site']: () =>
      firingAmplitudeEvents('Talentpool_Job Site', 'Job Serve', workspace, 'Candidates'),
    ['CW Jobs']: () =>
      firingAmplitudeEvents('Talentpool_CW Jobs', 'CV Library', workspace, 'Candidates'),
    ['Monster US & CA']: () =>
      firingAmplitudeEvents('Talentpool_Monster', 'Monster', workspace, 'Candidates'),
  };

  const navigate = useNavigate();
  const currentLocation = useLocation();

  const onChangeUS = (index: number, value: string, url: string) => {
    selectedIndexValue(index, value);
    navigate(url);
  };
  const onChangeUK = (index: number, value: string, url: string) => {
    selectedIndexValue(index, value);
    navigate(url);
  };
  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <BoxNavTheme>
        <List sx={{ width: '100%', pb: 2, p: 2 }} component="nav">
          <Box px={1} pt={0.5} my={1} bgcolor={WsColors.TheBlues50} justifyContent="space-evenly">
            <Grid item xs={10} container alignItems="center">
              <Box p={0.5}>
                <img src={Branding} alt="wisestep" />
              </Box>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                Wisestep
              </WsTypography>
            </Grid>
          </Box>
          <Box py={1}>
            <Divider />
          </Box>
          {talentPool.map((jobBoard, index) => (
            <ListItemButton
              sx={{
                background:
                  decodeURI(currentLocation.pathname) === jobBoard.url
                    ? WsColors.TheBlues100
                    : WsColors.White,
                p: 0.1,
              }}
              key={index}
              onClick={() => onChangeUS(index, jobBoard.name, jobBoard.url ?? '')}
            >
              <ListItemText
                primary={
                  <Grid item xs={10} container m={1}>
                    <Box pr={1}>{jobBoard.icon}</Box>
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={WsTypoColors.BLACK}
                      cursor="pointer"
                    >
                      {jobBoard.name}
                    </WsTypography>
                  </Grid>
                }
              />
              <WsTypography variant={WsTypoVariants.SUB_TITLE} cursor="pointer">
                {jobBoard.label ?? ''}
              </WsTypography>
            </ListItemButton>
          ))}
          <Box py={1}>
            <Divider />
          </Box>
          <Box className="integrated-database-search">
            <Box bgcolor={WsColors.TheBlues50} p={1}>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                US Job Boards
              </WsTypography>
            </Box>
            <Box py={1}>
              <Divider />
            </Box>
            {jobBoardsListUS.map((jobBoard, index) => (
              <ListItemButton
                sx={{
                  background:
                    `${currentLocation.pathname}${currentLocation.search}` === jobBoard.url
                      ? WsColors.TheBlues100
                      : WsColors.White,
                  p: 0.1,
                }}
                key={index}
                onClick={() => {
                  firingJobAmplitudeEvent[jobBoard.name]();
                  onChangeUS(index, jobBoard.name, jobBoard.url ?? '');
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center">
                      <Box pr={1}>{jobBoard.icon}</Box>
                      <WsTypography
                        variant={WsTypoVariants.BODY2}
                        color={WsTypoColors.BLACK}
                        cursor="pointer"
                      >
                        {jobBoard.name}
                      </WsTypography>
                    </Stack>
                  }
                />
                <WsChip value={'Search'} variant={WsChipVariants.PRIMARY} isPointer={true} />
              </ListItemButton>
            ))}
            <Box py={1}>
              <Divider />
            </Box>
            <Box bgcolor={WsColors.TheBlues50} p={1}>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                UK Job Boards
              </WsTypography>
            </Box>
            <Box py={1}>
              <Divider />
            </Box>
            {jobBoardsListUK.map((jobBoard, index) => (
              <ListItemButton
                sx={{
                  background:
                    `${currentLocation.pathname}${currentLocation.search}` === jobBoard.url
                      ? WsColors.TheBlues100
                      : WsColors.White,
                  p: 0.1,
                }}
                key={index}
                onClick={() => {
                  firingJobAmplitudeEvent[jobBoard.name]();
                  onChangeUK(index, jobBoard.name, jobBoard.url ?? '');
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center">
                      <Box pr={1}>{jobBoard.icon}</Box>
                      <WsTypography
                        variant={WsTypoVariants.BODY2}
                        color={WsTypoColors.BLACK}
                        cursor="pointer"
                      >
                        {jobBoard.name}
                      </WsTypography>
                    </Stack>
                  }
                />
                <WsChip value={'Search'} variant={WsChipVariants.PRIMARY} isPointer={true} />
              </ListItemButton>
            ))}
          </Box>
        </List>
      </BoxNavTheme>
    </ErrorBoundary>
  );
};
export default TalentPoolListSideNav;
